<template>
  <v-app>
    <v-snackbar
      v-model="snackbar"
      top
      color="success"
    >
      Data sent with success!
      <template v-slot:action="{ attrs }">
        <v-btn
          dark
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar
      v-model="snackbarError"
      top
      color="error"
    >
      Error submitting data
      <template v-slot:action="{ attrs }">
        <v-btn
          dark
          text
          v-bind="attrs"
          @click="snackbarError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-container fill-height fluid v-if="loadingFormData">
      <v-row
        align="center"
        justify="center">
        <v-col
          align="center"
          justify="center">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              :size="50"
              color="primary"
            ></v-progress-circular>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-form v-else @submit.prevent="submitForm" v-model="isValid" ref="form">
      <v-container>
        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="3" v-if="metadata">
            <div class="text-h3">{{ metadata.title }}</div>
            <div class="text-h6 mt-4">{{ metadata.preamble }}</div>
          </v-col>
        </v-row>
        <v-row v-for="(field, index) in fields" :key="field.name" class="justify-center">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-if="field.type === 'text' || field.type === 'email' || field.type === 'password'"
              :label="getFieldLabel(field)"
              :type="field.type"
              v-model="field.value"
              :rules="getFieldRules(field)"
              :required="isFieldRequired(field)"
              :placeholder="field.placeholder"
            ></v-text-field>
            <v-textarea
              v-else-if="field.type === 'textarea'"
              :label="getFieldLabel(field)"
              v-model="fields[index].value"
              :rules="getFieldRules(field)"
              :required="isFieldRequired(field)"
              :placeholder="field.placeholder"
            ></v-textarea>

            <template v-else-if="field.type === 'radio'">
              <div class="subtitle-1 mt-4">{{ getFieldLabel(field) }}</div>
              <v-radio-group
                v-model="fields[index].value"
                :rules="getFieldRules(field)">
                <v-radio
                  v-for="n in field.items"
                  :key="n"
                  :label="n"
                  :value="n"
                ></v-radio>
              </v-radio-group>
            </template>

            <template v-else-if="field.type === 'checkbox'">
              <div class="subtitle-1 mt-4">{{ getFieldLabel(field) }}</div>
              <v-checkbox
                v-for="(n, checkboxIndex) in fields[index].items"
                :key="n"
                v-model="fields[index].value"
                :label="n"
                :value="n"
                :hide-details="checkboxIndex + 1 < fields[index].items.length"
                :rules="getItemsFieldRules(field)">
              </v-checkbox>
            </template>

            <v-select
              v-else-if="field.type === 'select' || field.type === 'multiselect'"
              :items="fields[index].items"
              v-model="fields[index].value"
              :label="getFieldLabel(field)"
              :placeholder="field.placeholder"
              :multiple="field.type === 'multiselect'"
              :rules="getSelectFieldRules(field)"
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="3">
            <v-btn
              color="primary"
              type="submit"
              :disabled="!isValid"
              :loading="loading"
            >Submit</v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-app>
</template>

<script>
// import HelloWorld from './components/HelloWorld'
import { $http } from '@/services/http-common'

export default {
  name: 'App',
  components: {
    // HelloWorld
  },
  async created () {
    this.loadingFormData = true
    const { data } = await $http.get('/api/sheets')
    this.loadingFormData = false
    this.fields = data.fields
    this.metadata = data.metadata
    document.title = this.metadata.title
  },
  data: () => ({
    isValid: false,
    loading: false,
    loadingFormData: false,
    metadata: null,
    fields: [],
    rules: {
      checkboxRequired: values => values.length > 0 || 'Please select at least one item',
      required: value => !!value || 'Required',
      counter: (maxLength) => value => (value || '').length <= maxLength || `Max ${maxLength} characters`,
      email: value => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return pattern.test(value) || 'Invalid e-mail.'
      }
    },
    snackbar: false,
    snackbarError: false
  }),
  methods: {
    getFieldLabel (field) {
      if (field.validations && field.validations.required) {
        return `${field.label}*`
      }
      return field.label
    },
    getFieldRules (row) {
      const validations = []
      if (row.validations) {
        if (row.validations.maxLength) {
          validations.push(this.rules.counter(row.validations.maxLength))
        }
        if (row.validations.required) {
          validations.push(this.rules.required)
        }
      }
      if (row.type === 'email') {
        validations.push(this.rules.email)
      }
      return validations
    },
    getItemsFieldRules (field) {
      const validations = []
      if (field.validations) {
        if (field.validations.required) {
          validations.push(this.rules.checkboxRequired)
        }
      }
      return validations
    },
    getSelectFieldRules (field) {
      if (field.type === 'select') {
        return this.getFieldRules(field)
      }
      if (field.type === 'multiselect') {
        return this.getItemsFieldRules(field)
      }
      return []
    },
    isFieldRequired (field) {
      return field.validations && field.validations.required
    },
    async submitForm () {
      try {
        this.loading = true
        await $http.post('/api/sheets', {
          fields: this.fields
        })
        this.$refs.form.reset()
        this.snackbar = true
      } catch (err) {
        this.snackbarError = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
